import { z } from 'zod/v4';
import type { operations } from '@molchanovs/api-contract';
import { api, ApiError } from '@shared/api';
import { toInvoicePaymentsRequest, type InvoicePaymentsSelection } from './invoice-payments';

type ExportInput = NonNullable<
  operations['createExport']['requestBody']
>['content']['application/json'];
export type InvoiceExportInput = ExportInput;
export type InvoiceExportFormat = InvoiceExportInput['format'];
type AcceptanceResponse =
  operations['createExport']['responses'][202]['content']['application/json'];
export type InvoiceExportStatusResponse =
  operations['getRequesterExport']['responses'][200]['content']['application/json'];
export type InvoiceExportStatus = InvoiceExportStatusResponse['data'];

const decimal = z.string().regex(/^\d+(?:\.\d{1,2})?$/);
const instant = z.iso.datetime({ offset: true });
const filtersSchema = z.strictObject({
  amountEqualTo: decimal.optional(),
  amountGreaterThan: decimal.optional(),
  amountLessThan: decimal.optional(),
  paymentDateFrom: instant.optional(),
  paymentDateTo: instant.optional(),
  paymentType: z.string().min(1).optional(),
  origin: z.string().min(1).optional(),
  q: z.string().max(200).optional(),
  itemType: z.string().min(1).optional(),
  membershipTypeId: z.uuid().optional(),
  trainingProgramId: z.uuid().optional(),
  registrationId: z.uuid().optional(),
  userProfileId: z.uuid().optional(),
});
const selectionSchema = {
  filters: filtersSchema,
  sort: z.enum(['paymentDate', '-paymentDate', 'amountPaid', '-amountPaid']).optional(),
};
const requesterTimeZone = z
  .string()
  .min(1)
  .max(128)
  .refine((value) => {
    if (!/^[A-Za-z][A-Za-z0-9_+\-/]*$/u.test(value)) return false;
    try {
      new Intl.DateTimeFormat('en', { timeZone: value });
      return true;
    } catch {
      return false;
    }
  });
export const invoiceExportInputSchema = z.discriminatedUnion('format', [
  z.strictObject({
    source: z.literal('payments'),
    format: z.literal('csv'),
    parameters: z.strictObject(selectionSchema),
  }),
  z.strictObject({
    source: z.literal('payments'),
    format: z.literal('pdf'),
    parameters: z.strictObject({
      ...selectionSchema,
      presentation: z.strictObject({
        timezoneMode: z.literal('requester'),
        timeZone: requesterTimeZone,
      }),
    }),
  }),
]);
export const invoiceExportAcceptanceSchema = z.object({
  data: z.object({
    id: z.uuid(),
    status: z.literal('queued'),
    acceptedAt: instant,
    monitorUrl: z.string().min(1),
  }),
});
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const invoiceExportStatusSchema = z.object({
  data: z
    .object({
      id: z.uuid(),
      source: z.literal('payments'),
      format: z.enum(['csv', 'pdf']),
      status: z.enum(['queued', 'processing', 'ready', 'failed', 'expired']),
      acceptedAt: instant,
      startedAt: instant.nullable(),
      completedAt: instant.nullable(),
      publishedAt: instant.nullable(),
      expiresAt: instant.nullable(),
      selectedRecordCount: count.nullable(),
      artifact: z
        .object({
          filename: z.string().min(1),
          mediaType: z.enum(['text/csv', 'application/pdf']),
          byteSize: count,
          outputRowCount: count,
          downloadUrl: z.string().min(1),
        })
        .nullable(),
      failure: z
        .object({
          code: z.enum([
            'source_unavailable',
            'permission_revoked',
            'selection_empty',
            'generation_failed',
            'generation_timed_out',
            'adapter_version_unavailable',
          ]),
        })
        .nullable(),
    })
    .refine((data) =>
      data.status === 'ready'
        ? data.artifact !== null &&
          data.artifact.mediaType === exportMediaType(data.format) &&
          data.expiresAt !== null &&
          data.publishedAt !== null &&
          data.completedAt !== null &&
          data.failure === null
        : data.artifact === null &&
          (data.status === 'failed' ? data.failure !== null : data.failure === null)
    ),
});

function exportMediaType(format: InvoiceExportFormat): 'text/csv' | 'application/pdf' {
  return format === 'pdf' ? 'application/pdf' : 'text/csv';
}

/** Keep list selection semantics; capture PDF presentation separately from the list UTC toggle. */
export function toInvoiceExportInput(
  selection: InvoicePaymentsSelection,
  format: InvoiceExportFormat = 'csv'
): InvoiceExportInput {
  const request = toInvoicePaymentsRequest(selection);
  const { sort } = request;
  const filters = { ...request };
  delete filters.cursor;
  delete filters.limit;
  delete filters.sort;
  return invoiceExportInputSchema.parse({
    source: 'payments',
    format,
    parameters: {
      filters,
      sort,
      ...(format === 'pdf'
        ? {
            presentation: {
              timezoneMode: 'requester',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          }
        : {}),
    },
  });
}

export function createInvoiceExport(
  input: InvoiceExportInput,
  key: string
): Promise<AcceptanceResponse> {
  z.uuid().parse(key);
  return api.post('/api/v1/exports', invoiceExportInputSchema.parse(input), {
    headers: { 'Idempotency-Key': key },
    schema: invoiceExportAcceptanceSchema,
    keepalive: true,
  });
}

export function getInvoiceExport(
  id: string,
  signal: AbortSignal,
  format: InvoiceExportFormat = 'csv'
): Promise<InvoiceExportStatusResponse> {
  z.uuid().parse(id);
  return api
    .get(`/api/v1/exports/${id}`, { signal, schema: invoiceExportStatusSchema })
    .then((response) => {
      if (response.data.id !== id || response.data.format !== format)
        throw new ApiError({
          message: 'Invalid export identity',
          status: 0,
          code: 'VALIDATION_ERROR',
        });
      return response;
    });
}

/** Use the addressed protected endpoint, never a provider-supplied URL or public redirect. */
export async function downloadInvoiceExport(
  status: InvoiceExportStatus,
  signal: AbortSignal
): Promise<void> {
  if (
    !status.artifact ||
    status.status !== 'ready' ||
    !status.expiresAt ||
    Date.parse(status.expiresAt) <= Date.now()
  ) {
    throw new ApiError({ message: 'Export unavailable', status: 410, code: 'export_unavailable' });
  }
  z.uuid().parse(status.id);
  const blob = await api.getBlob(`/api/v1/exports/${status.id}/file`, {
    signal,
    headers: { Accept: exportMediaType(status.format) },
  });
  if (signal.aborted) return;
  if (
    blob.size !== status.artifact.byteSize ||
    blob.type.split(';')[0]?.trim().toLowerCase() !== exportMediaType(status.format)
  ) {
    throw new ApiError({ message: 'Incomplete export download', status: 0, code: 'PARSE_ERROR' });
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = Array.from(status.artifact.filename, (character) =>
    character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127 || '/\\'.includes(character)
      ? '_'
      : character
  ).join('');
  link.href = url;
  link.click();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1_000);
}

export const invoiceExportKeys = {
  status: (
    profile: string,
    workflow: string,
    viewerAccessScope: string,
    id: string,
    format: InvoiceExportFormat = 'csv'
  ) => ['invoice-export', profile, workflow, viewerAccessScope, id, format] as const,
};

export function invoiceExportErrorKey(
  error: unknown
): `invoices.csv.${'error' | 'accessLost' | 'expired' | 'unavailable' | 'conflict'}` {
  if (!(error instanceof ApiError)) return `invoices.csv.error`;
  if (error.status === 401 || error.status === 403 || error.status === 404)
    return `invoices.csv.accessLost`;
  if (error.status === 410) return `invoices.csv.expired`;
  if (error.status === 503 || error.status === 422) return `invoices.csv.unavailable`;
  if (error.code === 'idempotency_key_reused') return `invoices.csv.conflict`;
  return `invoices.csv.error`;
}
